import { combineActions, handleActions } from 'redux-actions';
import { getAuthToken, getBidderId } from './user';
import { getBidderPreferences, postBidderPreferences } from '@/redux/api/bidder';
import {
    LOAD_BIDDER_PREFERENCE_FAIL,
    LOAD_BIDDER_PREFERENCE_REQUEST,
    LOAD_BIDDER_PREFERENCE_SUCCESS,
    POST_BIDDER_PREFERENCE_FAIL,
    POST_BIDDER_PREFERENCE_REQUEST,
    POST_BIDDER_PREFERENCE_SUCCESS,
} from './actions';
import { ActionWithPayload } from '@/types/redux';
import { BidderPreference } from '@/types/BidderPreference';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';

export const LOADING_SAVED_SEARCH_ITEM_IDS = 'saved-search-items';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
export const DEFAULT_STATE = {
    bidderPreference: {},
    bidderPreferenceLoaded: undefined,
    bidderPreferenceLoading: false,
    error: undefined,
};

export type BidderPreferenceState = {
    bidderPreference: BidderPreference;
    bidderPreferenceLoaded?: Date;
    bidderPreferenceLoading?: boolean;
    error?: any;
};

export const bidderPreference = handleActions(
    {
        [combineActions(LOAD_BIDDER_PREFERENCE_FAIL, POST_BIDDER_PREFERENCE_FAIL)]: (
            state: BidderPreferenceState,
            action: ActionWithPayload<any>
        ) => ({
            ...state,
            bidderPreferenceLoaded: undefined,
            bidderPreferenceLoading: false,
            error: action.payload,
        }),
        [combineActions(LOAD_BIDDER_PREFERENCE_SUCCESS, POST_BIDDER_PREFERENCE_SUCCESS)]: (
            state: BidderPreferenceState,
            action: ActionWithPayload<any>
        ) => ({
            ...state,
            bidderPreference: action.payload,
            bidderPreferenceLoaded: new Date(),
            bidderPreferenceLoading: false,
        }),
        [LOAD_BIDDER_PREFERENCE_REQUEST]: (state: BidderPreferenceState) => ({
            ...state,
            bidderPreferenceLoaded: undefined,
            bidderPreferenceLoading: true,
        }),
        [POST_BIDDER_PREFERENCE_REQUEST]: (state: BidderPreferenceState) => ({
            ...state,
            bidderPreferenceLoading: true,
        }),
    },
    DEFAULT_STATE
);

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.bidderPreference;

export const bidderPreferenceSelector = createSelector(stateSelector, (state) => state.bidderPreference);
export const preferredCurrencySelector = createSelector(
    stateSelector,
    (state) => state.bidderPreference?.preferredCurrency
);
export const bidderPreferenceLoadedSelector = createSelector(stateSelector, (state) => state.bidderPreferenceLoaded);
export const bidderPreferenceLoadingSelector = createSelector(stateSelector, (state) => state.bidderPreferenceLoading);
export const bidderPreferenceErrorSelector = createSelector(stateSelector, (state) => state.error);

export const shouldFetchBidderPreferences = createSelector(
    bidderPreferenceLoadingSelector,
    bidderPreferenceLoadedSelector,
    (loading, loaded) => {
        if (loading) {
            return false;
        }
        const now = Date.now();
        return !loaded || now - loaded.getTime() > REDUX_STORE_TIME;
    }
);

/* ACTION CREATORS */
export const fetchBidderPreferencesIfNeeded = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchBidderPreferences(state)) {
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        try {
            dispatch({
                type: LOAD_BIDDER_PREFERENCE_REQUEST,
            });
            const response = await getBidderPreferences({ authToken, bidderId, deployment });
            dispatch({
                payload: response.payload,
                type: LOAD_BIDDER_PREFERENCE_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: LOAD_BIDDER_PREFERENCE_FAIL,
            });
        }
    }
    return Promise.resolve();
};

export const updateBidderPreferences =
    (bidderPreference: BidderPreference) => async (dispatch: Function, getState: Function) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        try {
            dispatch({
                type: POST_BIDDER_PREFERENCE_REQUEST,
            });
            const response = await postBidderPreferences({ authToken, bidderId, bidderPreference, deployment });
            dispatch({
                payload: response.payload,
                type: POST_BIDDER_PREFERENCE_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: POST_BIDDER_PREFERENCE_FAIL,
            });
        }
    };
